import type { BadgeProps } from '@fluentui/react-components';
import type { ReadinessResultRecord, ReadinessRunSummary } from '@lg/core';
import { READINESS_PERSONAS, type ReadinessCase, type ReadinessLayer } from '@lg/shared';
import { config } from '../config';

export const LAYER_LABEL: Record<ReadinessLayer, string> = {
  L1: 'Settings',
  L2: 'Access',
  L3: 'Process',
  L4: 'Data loads',
};

export const LAYER_QUESTION: Record<ReadinessLayer, string> = {
  L1: 'Does the new company match the template, apart from approved deviations?',
  L2: 'Can each role open what it needs, in this company only, and is it blocked from what it shouldn’t do?',
  L3: 'Can the role complete its everyday tasks, and do approvals route to the right seat?',
  L4: 'Did the copy and people packages land completely?',
};

export const RUN_COLOR: Record<ReadinessRunSummary['status'], BadgeProps['color']> = {
  Queued: 'informative',
  Running: 'brand',
  Passed: 'success',
  Failed: 'danger',
  Waived: 'warning',
};

export const RESULT_COLOR: Record<ReadinessResultRecord['status'], BadgeProps['color']> = {
  passed: 'success',
  failed: 'danger',
  skipped: 'subtle',
};

export const KIND_LABEL: Record<string, string> = {
  UiChange: 'UI change',
  Behavior: 'Behavior',
  Data: 'Data',
  Environment: 'Environment',
};

export const personaLabel = (key: string | null): string =>
  key ? (READINESS_PERSONAS.find((p) => p.key === key)?.label ?? key) : 'OData';

export function duration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  return `${m} min ${String(s % 60).padStart(2, '0')} s`;
}

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

/** What the case will prove, in words: the "what we will prove" column. */
export function expectText(c: ReadinessCase): string {
  const e = c.expect;
  switch (c.template) {
    case 'T-CONF-01':
      return e['approvedDeviation']
        ? `${str(e['value'])} (approved deviation)`
        : e['value'] === e['templateValue']
          ? `Same as template: ${str(e['templateValue'])}`
          : str(e['value']);
    case 'T-LOAD-01':
      return 'Record count matches the template';
    case 'T-ACC-01':
      return `Opens ${str(c.data['forms'])}`;
    case 'T-ACC-02':
      return `Only ${c.company} available; ${str(e['refused'])} refused`;
    case 'T-ACC-03':
      return 'Blocked by security';
    case 'T-ACC-04':
      return 'Every user matches a tested bundle';
    case 'T-P2P-01':
      return 'PO is In review';
    case 'T-P2P-02':
      return e['approverSeat']
        ? `Routes to ${str(e['approverSeat'])} (${str(e['approverName'])})${e['outsideVenue'] ? ', outside the venue' : ''}`
        : `Gap: ${str(e['gap'])}`;
    case 'T-P2P-03':
      return 'PO approved, then confirmed';
    case 'T-AP-01':
      return `Routes: ${str(e['routedAs'])}`;
    case 'T-PRJ-01':
      return `Project created with dimension ${str(e['venueDimension'])}`;
    default:
      return 'Approved';
  }
}

/** Link to the case in the Playwright report: the runner's trace, or the demo report when configured. */
export function evidenceLink(result: ReadinessResultRecord): string | null {
  if (result.traceUrl) return result.traceUrl;
  if (!config.demoReportsUrl || result.status === 'skipped') return null;
  return `${config.demoReportsUrl}/index.html#?q=${encodeURIComponent(result.caseId)}`;
}
